<script setup>
import { ref } from 'vue';

import Button from '../ui/base-button.vue';
import { Input as AInput, FormItem as AFormItem } from 'ant-design-vue';
import Checkbox from '../ui/base-checkbox.vue';

import { useToast } from 'vue-toastification';
import { useField, useForm } from 'vee-validate';

import axios from 'axios';
import { validationSchema } from './scheme';
import { toTypedSchema } from '@vee-validate/zod';

const props = defineProps(["uniqId", "scrollId"]);
const toaster = useToast();
const keepValuesOnUnmount = ref(true);
const isBlocked = ref(false);

const {handleSubmit, isSubmitting, meta} = useForm({
    keepValuesOnUnmount: keepValuesOnUnmount,
    validationSchema: toTypedSchema(validationSchema)
});

const {value: name, errorMessage: nameErrorMessage} = useField("name");
const {value: phone, errorMessage: phoneErrorMessage} = useField("phone")
const {value: email, errorMessage: emailErrorMessage} = useField("email")
const {value: activity, errorMessage: activityErrorMessage} = useField("activity");
const {value: agreed, errorMessage: agreedErrorMessage} = useField("agreed");

const handleSubmitForm = handleSubmit(async (form) => {
    const {data} = await axios.post(`V1/contact-form`, form);
    isBlocked.value = true;

    if (data.success) {       
        toaster.success(data.message, {
            position: "top-right"
        })
    } else {
        toaster.error(data.message, {
            position: "top-right"
        })
    }

    setTimeout(() => {
        isBlocked.value = false;
    }, 3000)
});
</script>

<template>
    <form class="form" :id="props.scrollId" @submit.prevent="handleSubmitForm">
        <h2 class="title">
            <i18n-t keypath="business.form.title">
                <template #break>
                    <br/>
                </template>
            </i18n-t> 
        </h2>

        <div class="inputs">
            <AFormItem
                :validate-status="nameErrorMessage"
                :help="nameErrorMessage ? $t(nameErrorMessage) : ''"
                class="form-item"
            >
                <AInput
                    :placeholder="$t('business.form.placeholders.name')"
                    v-model:value="name"
                    class="questionnaire_card_input"
                    :class="nameErrorMessage ? 'error' : ''"
                    :maxlength="254"
                />
            </AFormItem>

            <AFormItem
                :validate-status="phoneErrorMessage"
                :help="phoneErrorMessage ? $t(phoneErrorMessage) : ''"
                class="form-item"
            >
                <AInput
                    :placeholder="$t('business.form.placeholders.phone')"
                    v-model:value="phone"
                    class="questionnaire_card_input"
                    :class="phoneErrorMessage ? 'error' : ''"
                    v-maska="'+7 (###) ###-##-##'"
                    :maxlength="254"
                />
            </AFormItem>

            <AFormItem
                :validate-status="emailErrorMessage"
                :help="emailErrorMessage ? $t(emailErrorMessage) : ''"
                class="form-item"
            >
                <AInput
                    :placeholder="$t('business.form.placeholders.email')"
                    v-model:value="email"
                    class="questionnaire_card_input"
                    :class="emailErrorMessage ? 'error' : ''"
                    :maxlength="254"
                />
            </AFormItem>

            <AFormItem
                :validate-activity="activityErrorMessage"
                :help="activityErrorMessage ? $t(activityErrorMessage) : ''"
                class="form-item"
            >
                <AInput
                    :placeholder="$t('business.form.placeholders.activity')"
                    v-model:value="activity"
                    class="questionnaire_card_input"
                    :class="activityErrorMessage ? 'error' : ''"
                    :maxlength="254"
                />
            </AFormItem>
        </div>

        <Checkbox
            :text="$t('business.form.confirm-text')"
            :uniq-id="props.uniqId"
            v-model="agreed"
            name='agreed'
            :class="agreedErrorMessage ? 'error-checkbox' : ''"
        />

        <Button :disabled="isSubmitting || !meta.valid || isBlocked">
            {{ $t("business.form.send") }}
        </Button>
    </form>
</template>

<style scoped>
.form {
    display: flex;
    justify-content: center;
    align-items: center;

    flex-direction: column;

    height: 100%;

    padding: 30px;
    gap: 30px;
}

.inputs {
    display: grid;
    width: 100%;
}

.form-item {
    margin-bottom: 10px;
}

.error {
    border: 1px solid #F5010A;
}

.title {
    text-align: center;
    font-size: 30px;

    font-weight: 700;

    color: #535353;

    margin: 0;
}

@media screen and (max-width: 1280px) {
    .form {
        padding: 20px;
        gap: 30px;
    }

    .title {
        text-align: start;
    }
}
</style>